import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  downloadImportErrorsCsv,
  getImportJobStatus,
  useCancelImportJobMutation,
  useCloseImportSessionMutation,
  useCreateImportJobMutation,
  useDiscardImportDraftMutation,
  useImportJobStatus,
  useLaunchImportJobMutation,
  useOpenImportJobs,
  useStageImportImagesMutation,
  useUpdateImportJobDraftMutation,
} from '~entities/import-job/api/import-job-api';
import type {
  ImportJobSummary,
  SourcePlatform,
} from '~entities/import-job/api/types';
import { DiscardImportDraftDialog } from '~features/import-wizard/discard-import-draft-dialog';
import { ImportSessionIdBar } from '~features/import-wizard/import-session-id-bar';
import {
  clearWizardSessionStorage,
  hydrateImportDraft,
  photoMappingsFromStaged,
} from '~features/import-wizard/hydrate-from-draft';
import { WizardShell } from '~features/import-wizard/wizard-shell';
import { extractBothImagesFromContent } from '~features/email-editor';
import { useGetEmailTemplateQuery } from '~shared/api/email-template';
import { useUpdateEmailTemplateMutation } from '~shared/api/email-template';

import { Step0Welcome } from './step-0-welcome';
import { Step1Classes, type ClassWizardResult } from './step-1-classes';
import { Step2Photos, type ClassPhotoMapping } from './step-2-photos';
import { Step3Families, type FamilyWizardResult } from './step-3-families';
import { Step4Welcome } from './step-4-welcome';
import { Step5Review } from './step-5-review';
import { Button } from '~shared/shadcn/ui/button';

const WIZARD_SESSION_KEY = 'enrollio-import-wizard-session';
const importDataRouteApi = getRouteApi('/_auth/_layout/_settings/import-data');

type WizardSession = {
  jobId?: string;
  wizardSessionId: string;
  sourcePlatform?: SourcePlatform;
};

function loadSession(): WizardSession {
  try {
    const raw = localStorage.getItem(WIZARD_SESSION_KEY);
    if (raw) return JSON.parse(raw) as WizardSession;
  } catch {
    /* ignore */
  }
  return { wizardSessionId: crypto.randomUUID() };
}

function isSourcePlatform(value: unknown): value is SourcePlatform {
  return (
    typeof value === 'string' &&
    ['jackrabbit', 'studio_pro', 'studio_director', 'dsp', 'csv'].includes(value)
  );
}

export function ImportWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { step: stepFromUrl, platform: platformFromUrl } =
    importDataRouteApi.useSearch();
  const [step, setStepState] = useState(() => stepFromUrl ?? 0);
  const initialSession = loadSession();

  useEffect(() => {
    if (stepFromUrl != null) {
      setStepState(stepFromUrl);
    }
  }, [stepFromUrl]);

  const setStep = useCallback(
    (nextStep: number) => {
      setStepState(nextStep);
      void navigate({
        to: '/import-data',
        search: (prev) => ({
          step: nextStep > 0 ? nextStep : undefined,
          platform: prev.platform,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const [session, setSession] = useState(initialSession);
  const [jobId, setJobId] = useState<string | null>(session.jobId ?? null);
  const [sourcePlatform, setSourcePlatformState] = useState<
    SourcePlatform | null
  >(() => {
    if (isSourcePlatform(platformFromUrl)) return platformFromUrl;
    if (isSourcePlatform(session.sourcePlatform)) return session.sourcePlatform;
    return null;
  });
  const [classResult, setClassResult] = useState<ClassWizardResult | null>(null);
  const [photoMappings, setPhotoMappings] = useState<ClassPhotoMapping[]>([]);
  const [familyResult, setFamilyResult] = useState<FamilyWizardResult | null>(
    null,
  );
  // Default off: SCALR / studios prefer mass import without auto-blasting
  // welcome emails (ENR-4065). Staff can still turn this on in Step 4.
  const [welcomeEmailEnabled, setWelcomeEmailEnabled] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailParagraph1, setEmailParagraph1] = useState('');
  const [emailParagraph2, setEmailParagraph2] = useState('');
  const [hydrating, setHydrating] = useState(() => !!initialSession.jobId);
  const [hydrated, setHydrated] = useState(false);
  const [discardTargetJobId, setDiscardTargetJobId] = useState<string | null>(
    null,
  );
  const [closingJobId, setClosingJobId] = useState<string | null>(null);
  const [launchInitiated, setLaunchInitiated] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  const createJob = useCreateImportJobMutation();
  const updateDraft = useUpdateImportJobDraftMutation();
  const stageImages = useStageImportImagesMutation();
  const launchJob = useLaunchImportJobMutation();
  const closeSession = useCloseImportSessionMutation();
  const discardDraft = useDiscardImportDraftMutation();
  const cancelJob = useCancelImportJobMutation();
  const updateEmailTemplate = useUpdateEmailTemplateMutation();
  const { data: emailTemplate } = useGetEmailTemplateQuery();
  const { data: openImportJobs, refetch: refetchOpenJobs } = useOpenImportJobs(
    step === 0,
  );

  const { data: jobStatus, refetch: refetchJobStatus } = useImportJobStatus(
    jobId ?? '',
    !!jobId && step >= 5,
  );
  const { data: activeDraftStatus } = useImportJobStatus(
    jobId ?? '',
    !!jobId && step < 5,
  );
  const canDiscardDraft = activeDraftStatus?.status === 'draft';

  useEffect(() => {
    if (
      jobStatus?.status &&
      jobStatus.status !== 'draft'
    ) {
      setLaunchInitiated(true);
    }
  }, [jobStatus?.status]);

  const isLaunchBusy =
    launchInitiated ||
    launchJob.isPending ||
    createJob.isPending ||
    updateDraft.isPending ||
    stageImages.isPending ||
    updateEmailTemplate.isPending;

  const hydrateFromJobId = useCallback(
    async (targetJobId: string) => {
      setHydrating(true);
      try {
        const data = await hydrateImportDraft(targetJobId);
        if (!data) {
          clearWizardSessionStorage();
          setJobId(null);
          return;
        }

        setJobId(data.job.jobId);
        setSourcePlatformState(data.sourcePlatform);
        setClassResult(data.classResult);
        setFamilyResult(data.familyResult);
        setPhotoMappings(data.photoMappings);
        setWelcomeEmailEnabled(data.job.welcomeEmailEnabled);

        if (stepFromUrl == null && data.resumeStep > 0) {
          setStep(data.resumeStep);
        }
      } catch {
        clearWizardSessionStorage();
        setJobId(null);
      } finally {
        setHydrating(false);
        setHydrated(true);
      }
    },
    [setStep, stepFromUrl],
  );

  useEffect(() => {
    if (!session.jobId || hydrated) return;
    void hydrateFromJobId(session.jobId);
  }, [session.jobId, hydrated, hydrateFromJobId]);

  useEffect(() => {
    if (!emailTemplate) return;
    setEmailSubject((current) => current || emailTemplate.subject || '');
    setEmailParagraph1((current) => current || emailTemplate.paragraph1 || '');
    setEmailParagraph2((current) => current || emailTemplate.paragraph2 || '');
  }, [emailTemplate]);

  useEffect(() => {
    if (isSourcePlatform(platformFromUrl) && platformFromUrl !== sourcePlatform) {
      setSourcePlatformState(platformFromUrl);
    }
  }, [platformFromUrl, sourcePlatform]);

  const setSourcePlatform = useCallback(
    (platform: SourcePlatform) => {
      setSourcePlatformState(platform);
      void navigate({
        to: '/import-data',
        search: (prev) => ({
          step: prev.step,
          platform,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const activePlatform: SourcePlatform = sourcePlatform ?? 'jackrabbit';

  useEffect(() => {
    if (step > 0 && sourcePlatform == null) {
      toast.message('Select your previous system on step 1 first.');
      setStep(0);
    }
  }, [step, sourcePlatform, setStep]);

  useEffect(() => {
    localStorage.setItem(
      WIZARD_SESSION_KEY,
      JSON.stringify({
        jobId,
        wizardSessionId: session.wizardSessionId,
        sourcePlatform: sourcePlatform ?? undefined,
      } satisfies WizardSession),
    );
  }, [jobId, session.wizardSessionId, sourcePlatform]);

  const studentEstimate = useMemo(() => {
    if (!familyResult?.rows.length) return 0;
    return familyResult.rows.reduce((acc, row) => {
      let n = 0;
      for (let i = 1; i <= 5; i++) {
        const fn = row[`Student ${i} First Name` as keyof typeof row];
        if (fn && String(fn).trim()) n += 1;
      }
      return acc + n;
    }, 0);
  }, [familyResult]);

  const testParent = useMemo(() => {
    const row = familyResult?.rows[0];
    if (!row) return null;
    const email = String(row['Parent Email'] ?? '').trim();
    if (!email) return null;
    return {
      email,
      firstName: String(row['Parent First Name'] ?? '').trim(),
      lastName: String(row['Parent Last Name'] ?? '').trim(),
    };
  }, [familyResult]);

  const saveEmailTemplate = useCallback(async () => {
    const url1 = emailTemplate?.paragraph1ImageUrl ?? '';
    const url2 = emailTemplate?.paragraph2ImageUrl ?? '';
    // Restore merge tags per field. Do not fold paragraph2 into paragraph1 —
    // Send Test / launch would wipe the closing message (ENR-3077).
    const extracted1 = extractBothImagesFromContent(
      emailParagraph1,
      url1,
      url2,
    );
    const extracted2 = extractBothImagesFromContent(
      emailParagraph2,
      url1,
      url2,
      {
        para1: extracted1.text.includes('{{ paragraph1Image }}'),
        para2: extracted1.text.includes('{{ paragraph2Image }}'),
      },
    );
    const formData = new FormData();
    formData.append('subject', emailSubject);
    formData.append('paragraph1', extracted1.text);
    formData.append('paragraph2', extracted2.text);
    await updateEmailTemplate.mutateAsync({ template: formData });
  }, [
    emailSubject,
    emailParagraph1,
    emailParagraph2,
    emailTemplate?.paragraph1ImageUrl,
    emailTemplate?.paragraph2ImageUrl,
    updateEmailTemplate,
  ]);

  const persistDraft = useCallback(
    async (
      nextStep: number,
      overrides?: {
        classResult?: ClassWizardResult | null;
        familyResult?: FamilyWizardResult | null;
      },
    ): Promise<string | null> => {
      // Prefer explicit overrides — including intentional `null` on skip.
      // `??` would ignore `null` and keep the previous class/family result.
      const activeClassResult =
        overrides && 'classResult' in overrides
          ? overrides.classResult
          : classResult;
      const activeFamilyResult =
        overrides && 'familyResult' in overrides
          ? overrides.familyResult
          : familyResult;
      const clearClassCsv =
        overrides != null &&
        'classResult' in overrides &&
        overrides.classResult == null;
      const clearParentCsv =
        overrides != null &&
        'familyResult' in overrides &&
        overrides.familyResult == null;
      const config = {
        sourcePlatform: activePlatform,
        welcomeEmailEnabled,
        wizardSessionId: session.wizardSessionId,
        wizardStepReached: nextStep,
        duplicatePolicies: activeFamilyResult?.duplicatePolicies ?? {},
        ...(clearClassCsv ? { clearClassCsv: true } : {}),
        ...(clearParentCsv ? { clearParentCsv: true } : {}),
      };
      const files = {
        classCsv: activeClassResult?.file,
        parentCsv: activeFamilyResult?.file,
      };

      // Only draft jobs accept PATCH /draft. Stale jobIds (launched/cancelled)
      // must start a new draft so zip/CSV continue does not 400.
      let activeJobId = jobId;
      if (activeJobId) {
        try {
          const status = await getImportJobStatus(activeJobId);
          if (status.status !== 'draft' || status.sessionClosedAt) {
            activeJobId = null;
            setJobId(null);
          }
        } catch {
          activeJobId = null;
          setJobId(null);
        }
      }

      if (activeJobId) {
        await updateDraft.mutateAsync({
          jobId: activeJobId,
          config,
          files,
        });
        return activeJobId;
      }

      if (activeClassResult?.file || activeFamilyResult?.file) {
        const created = await createJob.mutateAsync({ config, files });
        setJobId(created.jobId);
        return created.jobId;
      }

      return null;
    },
    [
      activePlatform,
      welcomeEmailEnabled,
      session.wizardSessionId,
      familyResult,
      classResult,
      jobId,
      updateDraft,
      createJob,
    ],
  );

  const stagePendingPhotos = useCallback(
    async (activeJobId: string, mappings: ClassPhotoMapping[]) => {
      const pending = mappings.filter(
        (mapping): mapping is ClassPhotoMapping & { file: File } =>
          !!mapping.file,
      );
      if (!pending.length) return;

      const batchSize = 15;
      for (let i = 0; i < pending.length; i += batchSize) {
        const chunk = pending.slice(i, i + batchSize);
        await stageImages.mutateAsync({
          jobId: activeJobId,
          images: chunk.map((mapping) => ({
            file: mapping.file,
            rowIndex: mapping.rowIndex,
            classTitle: mapping.classTitle,
          })),
        });
      }

      const status = await getImportJobStatus(activeJobId);
      setPhotoMappings(photoMappingsFromStaged(status.stagedImages ?? []));
    },
    [stageImages],
  );

  const handleSaveExit = () => {
    void persistDraft(step)
      .then(async (activeJobId) => {
        if (activeJobId && photoMappings.some((m) => m.file)) {
          await stagePendingPhotos(activeJobId, photoMappings);
        }
      })
      .then(() => {
        toast.success('Progress saved', {
          description: 'Return anytime from Settings → Import Wizard.',
        });
        void refetchOpenJobs();
        void navigate({ to: '/dashboard' });
      });
  };

  const handleResumeDraft = (draft: ImportJobSummary) => {
    localStorage.setItem(
      WIZARD_SESSION_KEY,
      JSON.stringify({
        jobId: draft.jobId,
        wizardSessionId: draft.wizardSessionId,
        sourcePlatform: isSourcePlatform(draft.sourcePlatform)
          ? draft.sourcePlatform
          : undefined,
      } satisfies WizardSession),
    );
    setHydrated(false);
    void hydrateFromJobId(draft.jobId);
  };

  const handleCloseSession = async () => {
    if (!jobId) return;
    try {
      await closeSession.mutateAsync(jobId);
      resetWizardAfterSessionEnd();
      toast.success('Import session closed', {
        description: 'View details anytime in Import history.',
      });
      void refetchOpenJobs();
      void queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
      setStep(0);
    } catch {
      toast.error('Could not close import session');
    }
  };

  const resetWizardAfterSessionEnd = useCallback(() => {
    const nextSession: WizardSession = {
      wizardSessionId: crypto.randomUUID(),
    };
    clearWizardSessionStorage();
    localStorage.setItem(WIZARD_SESSION_KEY, JSON.stringify(nextSession));
    setSession(nextSession);
    setJobId(null);
    setClassResult(null);
    setFamilyResult(null);
    setPhotoMappings([]);
    setLaunchInitiated(false);
    setLaunchError(null);
    setHydrated(true);
  }, []);

  const abandonImportSession = useCallback(
    async (targetJobId: string, options?: { cancelFirst?: boolean }) => {
      setClosingJobId(targetJobId);
      try {
        if (options?.cancelFirst) {
          try {
            await cancelJob.mutateAsync(targetJobId);
          } catch {
            // Cancel may fail if already terminal; still try to close.
          }
        }
        // Idempotent if cancel already closed the session.
        await closeSession.mutateAsync(targetJobId);
        if (targetJobId === jobId) {
          resetWizardAfterSessionEnd();
          setStep(0);
        }
        toast.success('Import session closed', {
          description: 'You can start a new import anytime.',
        });
        void refetchOpenJobs();
        void queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
      } catch {
        toast.error('Could not close import session');
      } finally {
        setClosingJobId(null);
      }
    },
    [
      cancelJob,
      closeSession,
      jobId,
      queryClient,
      refetchOpenJobs,
      resetWizardAfterSessionEnd,
    ],
  );

  const handleCancelAndExit = async () => {
    if (!jobId) {
      resetWizardAfterSessionEnd();
      setStep(0);
      return;
    }
    await abandonImportSession(jobId, { cancelFirst: true });
  };

  const handleCloseOpenJob = async (draft: ImportJobSummary) => {
    const cancelFirst =
      draft.status === 'queued' || draft.status === 'processing';
    await abandonImportSession(draft.jobId, { cancelFirst });
  };

  const confirmDiscardDraft = async (targetJobId: string) => {
    try {
      await discardDraft.mutateAsync(targetJobId);
      if (targetJobId === jobId) {
        resetWizardAfterSessionEnd();
        setStep(0);
      }
      toast.success('Import draft discarded');
      void refetchOpenJobs();
      void queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
      setDiscardTargetJobId(null);
    } catch {
      toast.error('Could not discard import draft');
    }
  };

  const handleDiscardDraft = (targetJobId?: string) => {
    const id = targetJobId ?? jobId;
    if (!id) return;
    setDiscardTargetJobId(id);
  };

  const handleLaunch = async () => {
    const hasLocalCsv = !!(classResult?.file || familyResult?.file);

    if (!hasLocalCsv && !jobId) {
      toast.error('Could not start import', {
        description:
          'Upload at least one CSV — classes (step 1) or families (step 3).',
      });
      return;
    }

    setLaunchInitiated(true);
    setLaunchError(null);

    try {
      // Always pass current results so skipped (null) CSVs send clearClassCsv /
      // clearParentCsv — launch must not revive a stale draft CSV after skip.
      const activeJobId = await persistDraft(5, {
        classResult,
        familyResult,
      });
      if (!activeJobId) {
        const message =
          'Upload at least one CSV — classes (step 1) or families (step 3).';
        toast.error('Could not start import', { description: message });
        setLaunchError(message);
        return;
      }

      if (activeJobId !== jobId) {
        setJobId(activeJobId);
      }

      const draftStatus = await getImportJobStatus(activeJobId);
      if (!draftStatus.hasClassCsv && !draftStatus.hasParentCsv) {
        const message = hasLocalCsv
          ? 'Your CSV files did not save. Go back to steps 1 or 3 and re-upload.'
          : 'Upload at least one CSV — classes (step 1) or families (step 3).';
        toast.error('Could not start import', { description: message });
        setLaunchError(message);
        return;
      }

      if (photoMappings.some((m) => m.file)) {
        await stagePendingPhotos(activeJobId, photoMappings);
      }

      await saveEmailTemplate();

      await launchJob.mutateAsync(activeJobId);
      await refetchJobStatus();
      toast.message('Import started', {
        description: 'We\'ll keep working even if you leave this page.',
      });
    } catch (error) {
      const description = isAxiosError(error)
        ? ((error.response?.data as { error?: { message?: string } })?.error
            ?.message ??
          (error.response?.data as { message?: string })?.message)
        : undefined;
      toast.error('Could not start import', {
        description:
          description ?? 'Check your files and try again.',
      });
      setLaunchError(
        description ?? 'Could not start import. Check your files and try again.',
      );
    }
  };

  const handleDownloadErrors = async (dataset: 'classes' | 'parents') => {
    if (!jobId) return;
    try {
      const blob = await downloadImportErrorsCsv(jobId, dataset);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-${dataset}-errors.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download error file');
    }
  };

  if (hydrating) {
    return (
      <div className='flex min-h-[calc(100vh-4rem)] flex-col gap-4 p-6'>
        <ImportSessionIdBar
          jobId={jobId}
          wizardSessionId={session.wizardSessionId}
        />
        <div className='flex flex-1 flex-col items-center justify-center gap-3'>
          <Loader2 className='size-8 animate-spin text-muted-foreground' />
          <p className='text-muted-foreground'>Restoring your import session…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <DiscardImportDraftDialog
        open={discardTargetJobId != null}
        onOpenChange={(open) => {
          if (!open) setDiscardTargetJobId(null);
        }}
        isPending={discardDraft.isPending}
        onConfirm={() => {
          if (discardTargetJobId) {
            void confirmDiscardDraft(discardTargetJobId);
          }
        }}
      />
      <WizardShell
        currentStep={step}
        sourcePlatform={activePlatform}
        jobId={jobId}
        wizardSessionId={session.wizardSessionId}
        onSaveExit={step < 5 ? handleSaveExit : undefined}
        onDiscardDraft={
          canDiscardDraft ? () => handleDiscardDraft() : undefined
        }
        canDiscardDraft={canDiscardDraft}
      >
        {step === 0 && (
          <Step0Welcome
            value={sourcePlatform}
            openDrafts={openImportJobs ?? []}
            onResumeDraft={handleResumeDraft}
            onDiscardDraft={(draft) => handleDiscardDraft(draft.jobId)}
            onCloseSession={(draft) => void handleCloseOpenJob(draft)}
            closingJobId={closingJobId}
            onChange={setSourcePlatform}
          onNext={() => {
            if (!sourcePlatform) {
              toast.message('Choose your previous system to continue.');
              return;
            }
            setStep(1);
          }}
        />
      )}
      {step === 1 && sourcePlatform && (
        <Step1Classes
          sourcePlatform={sourcePlatform}
          restoredResult={classResult}
          onRestoredClear={() => setClassResult(null)}
          onBack={() => setStep(0)}
          onSkip={async () => {
            setClassResult(null);
            setPhotoMappings([]);
            try {
              await persistDraft(3, { classResult: null });
              setStep(3);
            } catch (error: unknown) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Failed to clear class import draft.';
              toast.error('Could not skip classes', {
                description: message,
              });
            }
          }}
          onComplete={async (result) => {
            if (!result?.file || result.rows.length === 0) {
              setClassResult(null);
              setPhotoMappings([]);
              try {
                await persistDraft(3, { classResult: null });
                setStep(3);
              } catch (error: unknown) {
                const message =
                  error instanceof Error
                    ? error.message
                    : 'Failed to clear class import draft.';
                toast.error('Could not continue without classes', {
                  description: message,
                });
              }
              return;
            }
            try {
              setClassResult(result);
              await persistDraft(2, { classResult: result });
              setStep(2);
            } catch (error: unknown) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Failed to save class import draft.';
              toast.error('Could not save classes', {
                description: message,
              });
            }
          }}
        />
      )}
      {step === 2 && (classResult?.titles.length ?? 0) > 0 && (
        <Step2Photos
          classTitles={classResult?.titles ?? []}
          mappings={photoMappings}
          onChange={setPhotoMappings}
          onBack={() => setStep(1)}
          onSkip={() => setStep(3)}
          onNext={async () => {
            try {
              const activeJobId = await persistDraft(3);
              if (activeJobId && photoMappings.some((m) => m.file)) {
                await stagePendingPhotos(activeJobId, photoMappings);
              }
              setStep(3);
            } catch (error: unknown) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Failed to upload class photos.';
              toast.error('Could not save class photos', {
                description: message,
              });
            }
          }}
        />
      )}
      {step === 2 && (classResult?.titles.length ?? 0) === 0 && (
        <div className='mx-auto max-w-4xl space-y-4 rounded-lg border p-6'>
          <h2 className='text-xl font-semibold'>Class photos</h2>
          <p className='text-sm text-muted-foreground'>
            Class photos stay unavailable until you finish mapping classes in the
            previous step. Go back to map a class CSV, or continue without
            photos.
          </p>
          <div className='flex justify-between'>
            <Button variant='outline' onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={() => setStep(3)}>Continue</Button>
          </div>
        </div>
      )}
      {step === 3 && sourcePlatform && (
        <Step3Families
          sourcePlatform={sourcePlatform}
          restoredResult={familyResult}
          onRestoredClear={() => setFamilyResult(null)}
          onBack={() => setStep(classResult ? 2 : 1)}
          onSkip={async () => {
            setFamilyResult(null);
            try {
              await persistDraft(4, { familyResult: null });
              setStep(4);
            } catch (error: unknown) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Failed to clear family import draft.';
              toast.error('Could not skip families', {
                description: message,
              });
            }
          }}
          onComplete={async (result) => {
            try {
              setFamilyResult(result);
              await persistDraft(4, { familyResult: result });
              setStep(4);
            } catch (error: unknown) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Failed to save family import draft.';
              toast.error('Could not save families', {
                description: message,
              });
            }
          }}
        />
      )}
      {step === 4 && (
        <Step4Welcome
          welcomeEmailEnabled={welcomeEmailEnabled}
          onWelcomeEmailEnabledChange={setWelcomeEmailEnabled}
          emailSubject={emailSubject}
          emailParagraph1={emailParagraph1}
          emailParagraph2={emailParagraph2}
          onEmailSubjectChange={setEmailSubject}
          testParentFirstName={testParent?.firstName}
          testParentLastName={testParent?.lastName}
          onSaveEmailTemplate={saveEmailTemplate}
          onBack={() => setStep(3)}
          onNext={async () => {
            try {
              await persistDraft(5);
              setStep(5);
            } catch (error: unknown) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Failed to save welcome email draft.';
              toast.error('Could not save welcome settings', {
                description: message,
              });
            }
          }}
        />
      )}
      {step === 5 && (
        <Step5Review
          classCount={classResult?.rows.length ?? 0}
          photoCount={
            photoMappings.filter((m) => m.file || m.staged).length
          }
          familyCount={familyResult?.familyCount ?? 0}
          studentEstimate={studentEstimate}
          welcomeEmailEnabled={welcomeEmailEnabled}
          job={jobStatus ?? null}
          isLaunchBusy={isLaunchBusy}
          launchInitiated={launchInitiated}
          launchError={launchError}
          isClosing={closeSession.isPending || closingJobId === jobId}
          isCancelling={cancelJob.isPending || closingJobId === jobId}
          onLaunch={() => void handleLaunch()}
          onCloseSession={() => void handleCloseSession()}
          onCancelAndExit={() => void handleCancelAndExit()}
          onBack={() => setStep(4)}
          onDownloadErrors={(d) => void handleDownloadErrors(d)}
        />
      )}
    </WizardShell>
    </>
  );
}
